import { graphql, readInlineData } from 'react-relay';

import { defineMessages, type IntlShape } from 'dibs-react-intl';
import { sessionStorage } from 'dibs-browser-storage';
import { trackEvent, eventNameConstants, interactionTypeConstants } from 'dibs-tracking';
const { EVENT_PROMO_INTERACTION } = eventNameConstants;
const { INTERACTION_TYPE_MESSAGE_BANNER } = interactionTypeConstants;

const BANNER_CLOSED_MANUALLY = 'bannerClosedManually';

export const isMessageBannerClosedManually = (): boolean =>
    sessionStorage.getItem(BANNER_CLOSED_MANUALLY) as boolean;

export const setIsBannerClosedManuallyInStorage = (): void => {
    sessionStorage.setItem(BANNER_CLOSED_MANUALLY, true);
};

import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { shouldShowExpiringPromo } from './expiringPromoMessageHelpers';

import { type MessageBannerContent_messages$data } from '../__generated__/MessageBannerContent_messages.graphql';
import {
    type sharedMessageHelpers_messages$data,
    type sharedMessageHelpers_messages$key,
} from './__generated__/sharedMessageHelpers_messages.graphql';
import { type sharedMessageHelpers_activePromotion$key } from './__generated__/sharedMessageHelpers_activePromotion.graphql';
import { type sharedMessageHelpers_expiringPromotion$key } from './__generated__/sharedMessageHelpers_expiringPromotion.graphql';

type Messages = NonNullable<ArrayElement<MessageBannerContent_messages$data>>;
type MessageItems = NonNullable<ArrayElement<Messages['items']>>;
export type MessageItem = NonNullable<MessageItems>;

type NonNullNote = NonNullable<MessageItem['note']>;
export type NoteObj = { note: NonNullNote };
type Anchors = NonNullable<NonNullNote['anchors']>;
export type Anchor = ArrayElement<Anchors>;

export type MessageItemTemplateData = Pick<
    NonNullable<NonNullNote['content']>,
    'cta' | 'message' | 'title'
> | null;

export type MessageItemTemplate = {
    desktop?: MessageItemTemplateData;
    mobile?: MessageItemTemplateData;
};

export const messageItemTemplate = ({
    desktop = null,
    mobile = null,
}: MessageItemTemplate): NoteObj => {
    return {
        note: {
            serviceId: 'template_id_1',
            content: desktop,
            anchors: [
                {
                    serviceId: 'template_id_2',
                    content: mobile,
                },
            ],
        },
    };
};

export const getMessageTitle = (messageItem: MessageItem): string =>
    messageItem?.note?.content?.title || '';

type TrackObjArgs = {
    messageItem: MessageItem;
    action: string;
    step_interaction_name: string;
    currentMessageTitle?: string;
};

export const trackMessageBanner = ({
    messageItem,
    action,
    step_interaction_name,
    currentMessageTitle,
}: TrackObjArgs): void => {
    const label = currentMessageTitle || getMessageTitle(messageItem);

    trackEvent(
        {
            category: 'promo interaction',
            action,
            label,
            eventName: EVENT_PROMO_INTERACTION,
            interaction_type: INTERACTION_TYPE_MESSAGE_BANNER,
            step_interaction_name: `${step_interaction_name} | ${label}`,
        },
        null
    );
};

export const isLowResolution = typeof window !== 'undefined' && window.innerWidth < 568; // To match scss mobile breakpoint size.

export type Cta = {
    text: string;
    url: string;
};

type NoteByDevice = NonNullable<ArrayElement<sharedMessageHelpers_messages$data['items']>>['note'];
type NoteByDeviceNote = {
    note: {
        serviceId: string | null;
        content: { message: string | null; title?: string | null; cta?: Cta | null } | null;
    };
};
type NoteByDeviceItem = {
    note: NoteByDevice;
};

export const getNoteByDevice = (
    messageItem: NoteByDeviceItem | null
): NoteByDeviceNote['note'] | null | undefined => {
    if (isLowResolution) {
        return messageItem?.note?.anchors?.[0];
    }
    return messageItem?.note;
};

const messagesFragment = graphql`
    fragment sharedMessageHelpers_messages on MessageSetModule @inline {
        items {
            note {
                serviceId
                content {
                    message
                }
                anchors {
                    serviceId
                    content {
                        message
                    }
                }
            }
        }
    }
`;

const activePromotionFragment = graphql`
    fragment sharedMessageHelpers_activePromotion on Promotion @inline {
        promotionMeta {
            customBannerMessage
            desktopBannerMessage
            desktopBannerCta
            mobileBannerMessage
            mobileBannerCta
        }
    }
`;

const expiringPromotionFragment = graphql`
    fragment sharedMessageHelpers_expiringPromotion on Promotion @inline {
        ...expiringPromoMessageHelpers_expiringPromotion
    }
`;

export const hasAnyText = (messageItem: NoteByDeviceItem): boolean => {
    const noteByDevice = getNoteByDevice(messageItem);
    return !!noteByDevice?.content?.message;
};

export const hasAnyMessagesByDevice = (
    messageSetRef: sharedMessageHelpers_messages$key[] = [],
    activePromotionRef: sharedMessageHelpers_activePromotion$key,
    expiringPromotionRef: sharedMessageHelpers_expiringPromotion$key
): boolean => {
    let hasPromoMessageByDevice = false;
    if (activePromotionRef || expiringPromotionRef) {
        const activePromotionData = readInlineData(activePromotionFragment, activePromotionRef);
        const expiringPromotionData = readInlineData(
            expiringPromotionFragment,
            expiringPromotionRef
        );
        const { desktopBannerMessage, mobileBannerMessage } =
            activePromotionData?.promotionMeta || {};
        hasPromoMessageByDevice =
            // `shouldShowExpiringPromo(userData)` seems like it will always return false
            // b/c it only looks for `expiringPromotion` and this query only has `activePromotion`
            shouldShowExpiringPromo(expiringPromotionData) ||
            (isLowResolution ? !!mobileBannerMessage : !!desktopBannerMessage);
    }
    return (
        hasPromoMessageByDevice ||
        messageSetRef.some(messageSetRefItem => {
            const { items } = readInlineData(messagesFragment, messageSetRefItem);
            return (items || []).filter(filterNulls).some(messageItem => hasAnyText(messageItem));
        })
    );
};

const fallbackText = defineMessages({
    fallback: {
        id: 'dbl.Header.MessageBanner.global_fallback_text',
        defaultMessage: "1stDibs is the world's leading marketplace for beautiful and rare objects",
    },
});
export const globalFallbackMessage = (intl: IntlShape): NoteObj => {
    const content = {
        title: 'global fallback',
        message: intl.formatMessage(fallbackText.fallback),
        cta: null,
    };
    return messageItemTemplate({
        desktop: content,
        mobile: content,
    });
};
